import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getCountFromServer,
  limit,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CoreService } from '../../core/service/core.service';
import { IStockTakeScheduleEntry } from '../models';
import {
  canAddStockTakeSchedule,
  STOCK_TAKE_MAX_SCHEDULE_COUNT,
  stockTakeScheduleLimitMessage,
} from '../utils/stock-take-limits.utils';

const scheduleDocPath = (storeId: string): string =>
  `operational/stores_data/${storeId}/data/stock_takes/stock_take_schedule`;

const schedulePath = (storeId: string): string =>
  `${scheduleDocPath(storeId)}/entries`;

/**
 * Firestore service for stock take schedule CRUD operations
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeScheduleService extends CoreService {
  constructor(firestore: Firestore) {
    super(firestore);
  }

  /**
   * Get schedule entries for a store (real-time), capped at {@link STOCK_TAKE_MAX_SCHEDULE_COUNT}.
   */
  getScheduleEntries(storeId: string): Observable<IStockTakeScheduleEntry[]> {
    const colRef = collection(this.firestore, schedulePath(storeId));
    const q = query(
      colRef,
      orderBy('createdAt', 'desc'),
      limit(STOCK_TAKE_MAX_SCHEDULE_COUNT),
    );
    return collectionData(q, { idField: 'id' }).pipe(
      map((docs: DocumentData[]) => docs as IStockTakeScheduleEntry[]),
    );
  }

  /**
   * Total schedule count for a store (used before create).
   */
  async getScheduleEntryCount(storeId: string): Promise<number> {
    const colRef = collection(this.firestore, schedulePath(storeId));
    const countSnap = await getCountFromServer(query(colRef));
    return countSnap.data().count;
  }

  /**
   * Create a new schedule entry.
   * Strips undefined values before writing so Firestore does not reject the payload.
   */
  async createScheduleEntry(storeId: string, entry: Omit<IStockTakeScheduleEntry, 'id'>): Promise<string> {
    const currentCount = await this.getScheduleEntryCount(storeId);
    if (!canAddStockTakeSchedule(currentCount)) {
      throw new Error(stockTakeScheduleLimitMessage());
    }
    const colRef = collection(this.firestore, schedulePath(storeId));
    const payload = this.stripUndefined({ ...entry, createdAt: Timestamp.now() });
    const docRef = await addDoc(colRef, payload);
    return docRef.id;
  }

  /**
   * Update an existing schedule entry.
   * Strips undefined values before writing.
   */
  async updateScheduleEntry(storeId: string, entryId: string, updates: Partial<IStockTakeScheduleEntry>): Promise<void> {
    const docRef = doc(this.firestore, `${schedulePath(storeId)}/${entryId}`);
    await updateDoc(docRef, this.stripUndefined(updates));
  }

  private stripUndefined<T extends object>(obj: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== undefined),
    ) as Partial<T>;
  }

  /**
   * Delete a schedule entry
   */
  async deleteScheduleEntry(storeId: string, entryId: string): Promise<void> {
    const docRef = doc(this.firestore, `${schedulePath(storeId)}/${entryId}`);
    await deleteDoc(docRef);
  }
}
